import React, { useState, useCallback } from 'react';
import '../styles/DataExchange.css';
import LoadingSpinner from './shared/LoadingSpinner';
import FileManager from './dataexchange/FileManager';
import PayrollChanges from './dataexchange/PayrollChanges';
import EmployeeManagement from './dataexchange/EmployeeManagement';
import PeriodSelector from './dataexchange/PeriodSelector';
import StatusIndicator from './dataexchange/StatusIndicator';
import { useDataExchange } from '../hooks/useDataExchange';
import { useUser } from '../contexts/UserContext';
import api from '../services/api';

// Konstanter
const FILE_TYPES = {
  INPUT: 'input',
  APPROVAL: 'approval',
  OUTPUT: 'output'
};

function DataExchange() {
  const [activeTab, setActiveTab] = useState('files');
  const [activeFileTab, setActiveFileTab] = useState('input');
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  const [tabChangeCheck, setTabChangeCheck] = useState(null);
  const { user } = useUser();
  const userPermissions = user ? {
    isAdmin: user.role === 'admin',
    isConsultant: user.role === 'consultant',
    isClient: user.role === 'client',
    canEdit: user.role === 'admin' || user.role === 'consultant' || user.role === 'client',
    canUpload: user.role === 'admin' || user.role === 'consultant'
  } : {
    isAdmin: false,
    isConsultant: false,
    isClient: false,
    canEdit: false,
    canUpload: false
  };

  const {
    selectedMonth,
    selectedYear,
    earliestAllowedDate,
    initialized,
    isLoadingFiles,
    inputStatus,
    inputFiles,
    approvalFiles,
    outputFiles,
    setSelectedMonth,
    setSelectedYear,
    setInputFiles,
    setApprovalFiles,
    setOutputFiles,
    isUploadAllowed,
    isEditingAllowed,
    setInputStatus
  } = useDataExchange();

  const canUploadInput = user && (user.role === 'admin' || user.role === 'consultant' || user.role === 'client');
  const canUploadOther = user && (user.role === 'admin' || user.role === 'consultant');

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);

  const handleTabChange = (newTab) => {
    if (activeTab === 'payroll' && tabChangeCheck) {
      // Tjek om der er ikke-gemte ændringer
      const canChange = tabChangeCheck();
      if (!canChange) {
        setPendingTab(newTab);
        setShowUnsavedChangesModal(true);
        return;
      }
    }
    setActiveTab(newTab);
  };

  const handleConfirmTabChange = () => {
    setShowUnsavedChangesModal(false);
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
  };

  const handleCancelTabChange = () => {
    setShowUnsavedChangesModal(false);
    setPendingTab(null);
  };

  const registerTabChangeCheck = useCallback((checkFn) => {
    setTabChangeCheck(() => checkFn);
  }, []);

  // Nye funktioner til at håndtere statusændringer
  const handleMarkReadyForProcessing = async () => {
    if (!inputStatus?.id) return;
    setActionLoading(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      await api.updatePayRunStatus(inputStatus.id, { payroll_status: 'processing' });
      setActionSuccess('Lønkørsel markeret klar til behandling.');
      setInputStatus(prev => ({ ...prev, payroll_status: 'processing' }));
    } catch (error) {
      setActionError('Kunne ikke markere klar til behandling.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkReadyForApproval = async () => {
    if (!inputStatus?.id) return;
    setActionLoading(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      await api.updatePayRunStatus(inputStatus.id, { payroll_status: 'waiting_approval' });
      setActionSuccess('Lønkørsel markeret klar til godkendelse.');
      setInputStatus(prev => ({ ...prev, payroll_status: 'waiting_approval' }));
    } catch (error) {
      setActionError('Kunne ikke markere klar til godkendelse.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprovePayRun = async () => {
    if (!inputStatus?.id) return;
    setActionLoading(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      await api.updatePayRunStatus(inputStatus.id, { payroll_status: 'approved' });
      setActionSuccess('Lønkørsel godkendt.');
      setInputStatus(prev => ({ ...prev, payroll_status: 'approved' }));
    } catch (error) {
      setActionError('Kunne ikke godkende løn.');
    } finally {
      setActionLoading(false);
    }
  };

  // Funktion til at bestemme hvilke knapper der skal vises
  const getStatusButtons = () => {
    if (!inputStatus || !inputStatus.id || inputStatus.status === "Lukket") {
      return null;
    }

    const isInitialStatus =
      inputStatus.payroll_status === null ||
      inputStatus.payroll_status === undefined ||
      inputStatus.payroll_status === '';

    return (
      <>
        {actionError && <span className="action-error">{actionError}</span>}
        {actionSuccess && <span className="action-success">{actionSuccess}</span>}
        {isInitialStatus ? (
          userPermissions.isClient && (
            <button
              className="status-action-btn process-btn"
              onClick={handleMarkReadyForProcessing}
              disabled={actionLoading}
            >
              {actionLoading ? 'Arbejder...' : 'Marker klar til behandling'}
            </button>
          )
        ) : inputStatus.payroll_status === 'processing' ? (
          (userPermissions.isConsultant || userPermissions.isAdmin) ? (
            <button
              className="status-action-btn approve-btn"
              onClick={handleMarkReadyForApproval}
              disabled={actionLoading}
            >
              {actionLoading ? 'Arbejder...' : 'Send til godkendelse'}
            </button>
          ) : userPermissions.isClient ? (
            <button
              className="status-action-btn process-btn active"
              disabled={true}
            >
              Behandles
            </button>
          ) : null
        ) : inputStatus.payroll_status === 'waiting_approval' ? (
          userPermissions.isClient && (
            <button
              className="status-action-btn approve-btn"
              onClick={handleApprovePayRun}
              disabled={actionLoading}
            >
              {actionLoading ? 'Arbejder...' : 'Godkend løn'}
            </button>
          )
        ) : inputStatus.payroll_status === 'approved' ? (
          <button
            className="status-action-btn approve-btn active"
            disabled={true}
          >
            Godkendt
          </button>
        ) : null}
      </>
    );
  };

  // Render loading state
  if (!initialized || selectedMonth === null || selectedYear === null) {
    return <LoadingSpinner />;
  }

  return (
    <div className="DataExchange">
      <div className="header-container">
        <div className="period-header">
          <h2>Dataudveksling</h2>
        </div>
        <div className="status-bar">
          <StatusIndicator 
            status={inputStatus?.status}
            deadline={inputStatus?.deadline}
            approvalMaterialDeadline={inputStatus?.approvalMaterialDeadline}
            approvalDeadline={inputStatus?.approvalDeadline}
            outputDeadline={inputStatus?.outputDeadline}
            payroll_status={inputStatus?.payroll_status}
          />
        </div>
      </div>

      <PeriodSelector
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        earliestAllowedDate={earliestAllowedDate}
        onMonthSelect={setSelectedMonth}
        onYearChange={setSelectedYear}
        actions={getStatusButtons()}
      />

      <div className="tabs-container">
        <div className="tabs-left">
          <button 
            className={`tab-button ${activeTab === 'files' ? 'active' : ''}`}
            onClick={() => handleTabChange('files')}
          >
            Fildeling
          </button>
          {userPermissions.canEdit && (
            <>
              <button 
                className={`tab-button ${activeTab === 'payroll' ? 'active' : ''}`}
                onClick={() => handleTabChange('payroll')}
              >
                Løndele
              </button>
              <button 
                className={`tab-button ${activeTab === 'employees' ? 'active' : ''}`}
                onClick={() => handleTabChange('employees')}
              >
                Nyansatte
              </button>
            </>
          )}
        </div>
      </div>

      {showUnsavedChangesModal && (
        <div className="unsaved-changes-modal">
          <div className="modal-content">
            <p>Du har ikke-gemte ændringer. Vil du fortsætte?</p>
            <div className="modal-actions">
              <button className="secondary-btn" onClick={handleCancelTabChange}>Bliv på siden</button>
              <button className="primary-btn" onClick={handleConfirmTabChange}>Fortsæt</button>
            </div>
          </div>
        </div>
      )}

      <div className="content">
        {activeTab === 'files' ? (
          <>
            <div className="subtabs-container">
              <button 
                className={`subtab-button ${activeFileTab === 'input' ? 'active' : ''}`}
                onClick={() => setActiveFileTab('input')}
              >
                Input
              </button>
              <button 
                className={`subtab-button ${activeFileTab === 'approval' ? 'active' : ''}`}
                onClick={() => setActiveFileTab('approval')}
              >
                Løn til godkendelse
              </button>
              <button 
                className={`subtab-button ${activeFileTab === 'output' ? 'active' : ''}`}
                onClick={() => setActiveFileTab('output')}
              >
                Output
              </button>
            </div>
            <div className="file-content">
              {isLoadingFiles ? (
                <div className="loading">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Henter filer...</span>
                </div>
              ) : (
                <>
                  {activeFileTab === 'input' && (
                    <FileManager
                      fileType={FILE_TYPES.INPUT}
                      files={inputFiles}
                      setFiles={setInputFiles}
                      canUpload={canUploadInput && isUploadAllowed()}
                      onFileUpdate={() => {}}
                      selectedMonth={selectedMonth}
                      selectedYear={selectedYear}
                    />
                  )}
                  {activeFileTab === 'approval' && (
                    <FileManager
                      fileType={FILE_TYPES.APPROVAL}
                      files={approvalFiles}
                      setFiles={setApprovalFiles}
                      canUpload={canUploadOther && isUploadAllowed()}
                      onFileUpdate={() => {}}
                      selectedMonth={selectedMonth}
                      selectedYear={selectedYear}
                    />
                  )}
                  {activeFileTab === 'output' && (
                    <FileManager
                      fileType={FILE_TYPES.OUTPUT}
                      files={outputFiles}
                      setFiles={setOutputFiles}
                      canUpload={canUploadOther && isUploadAllowed()}
                      onFileUpdate={() => {}}
                      selectedMonth={selectedMonth}
                      selectedYear={selectedYear}
                    />
                  )}
                </>
              )}
            </div>
          </>
        ) : activeTab === 'payroll' ? (
          <PayrollChanges
            isEditingAllowed={userPermissions.canEdit && isEditingAllowed()}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            onTabChange={registerTabChangeCheck}
          />
        ) : activeTab === 'employees' ? (
          <EmployeeManagement
            isEditingAllowed={userPermissions.canEdit && isEditingAllowed()}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
          />
        ) : null}
      </div>
    </div>
  );
}

export default DataExchange;